import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Container, Typography, Grid, Box, IconButton, Divider } from "@material-ui/core";
import FacebookIcon from "@material-ui/icons/Facebook";
import TwitterIcon from "@material-ui/icons/Twitter";
import LinkedInIcon from "@material-ui/icons/LinkedIn";
import InstagramIcon from "@material-ui/icons/Instagram";
import YouTubeIcon from "@material-ui/icons/YouTube";
import GitHubIcon from "@material-ui/icons/GitHub";

const useStyles = makeStyles((theme) => ({
  footer: {
    backgroundColor: "#1a1a1a",
    color: "#ffffff",
    padding: theme.spacing(8, 0, 3),
  },
  topSection: {
    marginBottom: theme.spacing(4),
  },
  logo: {
    height: 50,
    marginBottom: theme.spacing(3),
    objectFit: "contain",
  },
  columnTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    fontSize: "1.1rem",
    color: "#ffffff",
  },
  linkList: {
    listStyle: "none",
    padding: 0,
    margin: 0,
  },
  linkItem: {
    marginBottom: theme.spacing(1.5),
  },
  link: {
    color: "rgba(255,255,255,0.7)",
    textDecoration: "none",
    fontSize: "0.95rem",
    cursor: "pointer",
    transition: "color 0.2s ease",
    "&:hover": {
      color: theme.palette.primary.light,
    },
  },
  socialSection: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  socialIcon: {
    color: "rgba(255,255,255,0.7)",
    transition: "all 0.2s ease",
    "&:hover": {
      color: theme.palette.primary.light,
      transform: "translateY(-2px)",
    },
  },
  divider: {
    backgroundColor: "rgba(255,255,255,0.1)",
    margin: theme.spacing(4, 0, 3),
  },
  bottomSection: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      textAlign: "center",
    },
  },
  copyright: {
    color: "rgba(255,255,255,0.6)",
    fontSize: "0.9rem",
  },
  bottomLinks: {
    display: "flex",
    gap: theme.spacing(3),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "center",
    },
  },
  bottomLink: {
    color: "rgba(255,255,255,0.6)",
    textDecoration: "none",
    fontSize: "0.9rem",
    cursor: "pointer",
    transition: "color 0.2s ease",
    "&:hover": {
      color: theme.palette.primary.light,
    },
  },
}));

const socialIcons = {
  facebook: FacebookIcon,
  twitter: TwitterIcon,
  linkedin: LinkedInIcon,
  instagram: InstagramIcon,
  youtube: YouTubeIcon,
  github: GitHubIcon,
};

const FooterSection = ({ logo, columns = [], socialLinks = [], copyright, bottomLinks = [] }) => {
  const classes = useStyles();

  const handleLinkClick = (url, external) => {
    if (url.startsWith("#")) {
      const element = document.querySelector(url);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    } else if (external || !url.startsWith("/")) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      window.location.href = url;
    }
  };

  return (
    <Box className={classes.footer}>
      <Container>
        <Grid container spacing={4} className={classes.topSection}>
          {/* Logo and Social Links Column */}
          <Grid item xs={12} sm={6} md={3}>
            {logo && (
              <img
                src={logo.imageUrl}
                alt={logo.altText}
                className={classes.logo}
              />
            )}
            {socialLinks.length > 0 && (
              <div className={classes.socialSection}>
                {socialLinks.map((social, index) => {
                  const IconComponent = socialIcons[social.platform];
                  if (!IconComponent) return null;
                  
                  return (
                    <IconButton
                      key={index}
                      className={classes.socialIcon}
                      onClick={() => handleLinkClick(social.url, true)}
                      size="small"
                    >
                      <IconComponent />
                    </IconButton>
                  );
                })}
              </div>
            )}
          </Grid>

          {/* Link Columns */}
          {columns.map((column, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Typography className={classes.columnTitle}>
                {column.title}
              </Typography>
              <ul className={classes.linkList}>
                {column.links.map((link, linkIndex) => (
                  <li key={linkIndex} className={classes.linkItem}>
                    <a
                      className={classes.link}
                      onClick={() => handleLinkClick(link.url, link.external)}
                    >
                      {link.text}
                    </a>
                  </li>
                ))}
              </ul>
            </Grid>
          ))}
        </Grid>

        <Divider className={classes.divider} />

        {/* Bottom Section */}
        <div className={classes.bottomSection}>
          <Typography className={classes.copyright}>
            {copyright}
          </Typography>
          {bottomLinks.length > 0 && (
            <div className={classes.bottomLinks}>
              {bottomLinks.map((link, index) => (
                <a
                  key={index}
                  className={classes.bottomLink}
                  onClick={() => handleLinkClick(link.url, link.external)}
                >
                  {link.text}
                </a>
              ))}
            </div>
          )}
        </div>
      </Container>
    </Box>
  );
};

export default FooterSection;
